import React, { useEffect, useState } from 'react';
import useNotificationStore from '../stores/useNotificationStore';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';

const StudentNotificationPage = () => {
  const { user } = useAuthStore();
  const { notifications, markAsRead, markAllAsRead, isLoading } = useNotificationStore();

  const fetchNotifications = useNotificationStore.getState().fetchNotifications;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);


  useEffect(() => {
    fetchNotifications();
  }, []);


  const totalPages = Math.ceil(notifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, notifications.length);
  const currentNotifications = notifications.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
  if (!isLoading) {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const timer = setTimeout(scrollToTop, 50);
    return () => clearTimeout(timer);
  }
}, [currentPage, isLoading]);

  const handleItemsPerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setItemsPerPage(newPerPage);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <DashboardLayout userRole={user.role}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Notifications</h1>
          <button
            onClick={markAllAsRead}
            className="px-4 py-2 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-lg"
          >
            Mark All as Read
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔔</div>
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">No notifications</h3>
            <p className="text-neutral-600 dark:text-neutral-400">You’ll receive notifications here when you enroll in courses, earn badges, and more.</p>
          </div>
        ) : (
           <>
           <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Showing <span className="font-medium">{startIndex + 1}</span>–
                <span className="font-medium">{endIndex}</span> of{' '}
                <span className="font-medium">{notifications.length}</span> results
              </p>

              <div className="flex items-center gap-2">
                <label htmlFor="itemsPerPage" className="text-sm text-neutral-700 dark:text-neutral-300">
                  Per page:
                </label>
                <select
                  id="itemsPerPage"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-lg px-2 py-1 text-sm text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              {currentNotifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`p-4 rounded-lg border ${notification.isRead
                    ? 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700'
                    : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
                    }`}
                >
                  <div className="flex justify-between">
                    <p className="text-neutral-900 dark:text-white">{notification.message}</p>
                    {!notification.isRead && (
                      <button
                        onClick={() => markAsRead(notification._id)}
                        className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                    {new Date(notification.createdAt).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-8 gap-4">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm rounded-lg ${
                    currentPage === 1
                      ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 cursor-not-allowed'
                      : 'bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                  }`}
                >
                  Previous
                </button>

                <div className="flex flex-wrap justify-center gap-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium ${
                        currentPage === page
                          ? 'bg-primary-500 text-white'
                          : 'bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                      }`}


                       >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm rounded-lg ${
                    currentPage === totalPages
                      ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 cursor-not-allowed'
                      : 'bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StudentNotificationPage;